import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { useEffect, useRef, useState } from 'react'
import { TbPhotoX, TbX, TbZoom } from 'react-icons/tb'
import { useStoreContext } from '../../Contexts/StoreContext';
import Img from './Img';
import { useLayoutContext } from '../../Contexts/LayoutContext';
import { ImSpinner8 } from 'react-icons/im';
import { normalizeStringToSearch } from '../../Utils/Helpers';
import BadgeNewProduct from './Product/BadgeNewProduct';
import BadgeProductSale from './Product/BadgeProductSale';
import { useStoreHook } from '../../Hooks/StoreHook';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Button from '../Form/Button';

interface SearchProps {
  fromBottom?: Boolean;
  onSearching?: CallableFunction;
  alwaysDisplayCloseButton?: Boolean;
}

const Search = ({
  onSearching,
  fromBottom=true,
  alwaysDisplayCloseButton,
}: SearchProps) => {

  const { t } = useTranslation();
  const { productsToSearch, categories, showSearchBar, setShowSearchBar } = useLayoutContext();
  const [ showList, setShowList ] = useState<boolean>(false);
  const [query, setQuery] = useState('');
  const { currentStore } = useStoreContext();
  const inputSearchRef = useRef<HTMLInputElement>(null);
  const [readOnlySearch, setReadOnlySearch] = useState(false);
  const { getLinkToHome, openProductToAdd } = useStoreHook();
  const navigate = useNavigate();

  const handleChangeOption = (e: any) => {
    if (e) {
      setQuery('');
      setShowList(false);
      setShowSearchBar(false);

      if (e.slug) {
        navigate(`${getLinkToHome()}/produto/${e.slug}`);
      }

      openProductToAdd(e, e?.categories[0]);

      // Tira o foco do input após selecionar para não exibir o teclado
      inputSearchRef.current?.blur();
    }
  }

  const handleCancel = () => {
    setQuery('');
    setShowSearchBar(false);
  }

  useEffect(() => {
    if (query) {
      onSearching!(query.length > 0)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (showSearchBar) {
      setReadOnlySearch(false);
      if (fromBottom) {
        const timeout = setTimeout(() => {
          requestAnimationFrame(() => {
            inputSearchRef.current?.focus();
          });
        }, 300);
        return () => clearTimeout(timeout);
      }
    }
  }, [showSearchBar, fromBottom]);

  const filteredProducts =
    query === ''
      ? []
      : productsToSearch.filter((product: any) => {
          const queryNormalized = normalizeStringToSearch(query);
          const productNameMatch = normalizeStringToSearch(product.name).includes(queryNormalized);
          const categoryMatch = product.categories?.some((category: any) => normalizeStringToSearch(category.name).includes(queryNormalized));
          return productNameMatch || categoryMatch;
        });

  // const [showFilter, setShowFilter] = useState(false);
  return (
    <>
      <div className={`overflow-y-auto z-30 ring-1 ring-gray-100 w-full shadow bg-white ${!currentStore && 'hidden'}
        ${!fromBottom && ((query !== '' && showList) || (query !== '' && filteredProducts.length === 0)) ? 'rounded-t-lg' : 'rounded-lg'}
        ${fromBottom && ((query !== '' && showList) || (query !== '' && filteredProducts.length === 0)) ? 'rounded-t-none' : ''}
        `}>
          <Combobox
            onChange={(product) => {
              handleChangeOption(product);
            }}
            onClose={() => setQuery('')}
          >
            <div className="relative">
              {currentStore && categories.length > 0 && productsToSearch.length === 0
                ? <ImSpinner8 className="animate-spin pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-gray-400 z-10" />
                : <TbZoom className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-gray-400 z-10" aria-hidden="true"/>
              }

              <div className='relative flex items-center justify-between'>
                <ComboboxInput
                  ref={inputSearchRef} //* ref usado para focus somente na busca do footer
                  readOnly={readOnlySearch}
                  displayValue={() => query}
                  className={`
                      h-12 w-full border-0 bg-transparent pl-10 font-bold placeholder:font-normal placeholder:text-gray-400 focus:ring-0 disabled:bg-gray-100
                      ${query.length > 0 ? 'pr-8' : 'pr-4'}
                    `}
                  placeholder={`${currentStore && categories.length > 0 && productsToSearch.length === 0 ? t('layout.search.placeholder_loading') : t('layout.search.placeholder')}`}
                  onChange={(event) => {setQuery(event.target.value); setShowList(true)}}
                  onClick={() => setShowSearchBar(Boolean(fromBottom))}
                  onBlur={() => {
                    setShowList(false);
                    onSearching!(false);
                  }}
                  onFocus={() => onSearching?.(true)}
                  autoFocus={Boolean(showSearchBar && fromBottom)}
                  disabled={currentStore && categories.length === 0 && productsToSearch.length === 0}
                />

                <div className='flex items-center gap-0.5'>

                  {/* Filter Button */}
                  {/* <Button rounded onClick={() => setShowFilter(true)} className="w-8 h-8 ring-inset ring-transparent">
                    <div><TbFilter size={17} /></div>
                  </Button> */}

                  {/* Reset Button */}
                  {(query.length > 0 || alwaysDisplayCloseButton) &&
                    <Button rounded gray onClick={handleCancel} className="w-8 h-8 ring-inset ring-transparent hover:ring-transparent mr-3 cursor-pointer">
                      <div><TbX size={17} /></div>
                    </Button>
                  }

                </div>
              </div>
            </div>

            {(query && showList) ? (
              <ComboboxOptions static className={`
              absolute z-30 ${fromBottom
                ? 'bottom-16 left-3 right-3 rounded-t-xl shadow-reverse-md'
                : 'rounded-b-lg shadow-xl right-0 left-0' }
                max-h-[40vh] md:max-h-[60vh] scroll-py-2 overflow-y-auto text-gray-800 ring-1 ring-gray-100 divide-y divide-gray-100
                `}>
                {filteredProducts.map((product: any, index: number) => (
                  <ComboboxOption
                    key={`${product.id}-${index}`}
                    value={product}
                    className="flex items-center gap-1.5 cursor-default font-medium select-none px-1.5 py-3 data-[focus]:bg-custom-primary data-[focus]:text-white bg-white"
                  >
                    <div className='max-w-7 min-w-7 overflow-hidden'>
                      <Img
                        src={product.photos?.thumbnail}
                        fallback={
                          <div className='text-gray-500 flex items-center justify-center bg-gray-200 max-w-7 min-w-7 max-h-7 min-h-7 rounded'>
                            <TbPhotoX className='text-white' />
                          </div>
                        }
                        alt={product.name}
                        className="max-w-7 min-w-7 max-h-7 min-h-7 rounded"
                      />
                    </div>

                    <span className='w-full leading-4' dangerouslySetInnerHTML={{__html: product?.name?.replace(/<br\s*\/?>/gi, ' ') ?? ''}} />

                      {/* BADGE FOR NEW PRODUCT ON CATALOG */}
                      <BadgeNewProduct product={product} className='rounded' />

                      {/* PRODUCT SALE  */}
                      <BadgeProductSale product={product} className='rounded' />

                  </ComboboxOption>
                ))}
              </ComboboxOptions>
            ) : null}

            {query !== '' && filteredProducts.length === 0 && (
              <p className={`absolute w-full bg-white shadow p-4 text-gray-500 ring-1 ring-gray-100
                ${fromBottom ? 'bottom-16 rounded-t-xl shadow-reverse-xl rounded-t-lg left-3 right-3' : 'rounded-b-lg left-0 right-0'}
                `}>{t('products.no_products_found')}</p>
            )}
          </Combobox>
      </div>

      {/* <Modal open={showFilter} className="max-w-sm m-4 text-left" onClose={() => setShowFilter(false)}>
        <SearchFilter onClose={() => setShowFilter(false)} />
      </Modal> */}
    </>
  )
}

export default Search;